import { useCallback, useEffect, useRef, useState } from 'react';
import { staffApi, ApiError } from '../api';
import type { StaffInfoRequest } from '../types/staffInfoRequest';

/**
 * TEMPORARY — DELETE THIS FILE once the backend includes `infoRequestStatus`
 * on each StaffMember in `GET /staff?locationId=`.
 *
 * The status endpoint is per-staff (`GET /staff/{id}/info-request`), so showing
 * status on every row costs one request per staff member. This hook is the only
 * place that fanout exists: callers just get a Map<staffId, StaffInfoRequest>.
 * When the list response carries the status, replace this hook's body with a
 * read off StaffMember (or drop it and index the list directly) — no call site
 * has to change.
 */

const CONCURRENCY = 6;

const notSent = (staffId: number): StaffInfoRequest => ({
	staffId,
	status: 'NOT_SENT',
	requestId: null,
	requestedAt: null,
	completedAt: null,
	expiresAt: null,
});

/** Run `fn` over `items` with at most `limit` in flight at once. */
async function mapWithConcurrency<T, R>(
	items: T[],
	limit: number,
	fn: (item: T) => Promise<R>
): Promise<R[]> {
	const results = new Array<R>(items.length);
	let cursor = 0;

	const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (cursor < items.length) {
			const index = cursor;
			cursor += 1;
			results[index] = await fn(items[index]);
		}
	});

	await Promise.all(workers);
	return results;
}

/**
 * Map value semantics:
 *   undefined — not fetched yet
 *   null      — fetch failed; the status is genuinely unknown
 *   object    — a known status
 *
 * "Unknown" must stay distinct from NOT_SENT. Collapsing them would show an
 * actionable Request button whenever a status lookup blips, letting a provider
 * re-request from someone who already submitted their SSN.
 */
export type InfoRequestEntry = StaffInfoRequest | null;

export const useStaffInfoRequestStatuses = (staffIds: number[], locationId: number | null) => {
	const [statuses, setStatuses] = useState<Map<number, InfoRequestEntry>>(new Map());
	const [isLoading, setIsLoading] = useState(false);

	// Guards against a stale fanout resolving after the location changed.
	const runRef = useRef(0);

	// Keeps the effect's dep a primitive, so callers don't have to memoize the array.
	const idsKey = staffIds.join(',');
	const idsRef = useRef(staffIds);
	idsRef.current = staffIds;

	const fetchStatuses = useCallback(async () => {
		const ids = idsRef.current;

		if (!locationId || ids.length === 0) {
			// Bump the run counter so any in-flight fanout is discarded rather than
			// landing after this reset.
			runRef.current += 1;
			setStatuses(new Map());
			setIsLoading(false);
			return;
		}

		const run = runRef.current + 1;
		runRef.current = run;
		setIsLoading(true);

		const entries = await mapWithConcurrency(ids, CONCURRENCY, async (id): Promise<
			InfoRequestEntry
		> => {
			try {
				return await staffApi.getInfoRequest(id, locationId);
			} catch (err) {
				// A 404 is a real answer: no request was ever created.
				if (err instanceof ApiError && err.isNotFound()) {
					return notSent(id);
				}

				// Anything else means we don't know. Report it as unknown rather
				// than NOT_SENT, so the cell won't offer a Request button that
				// could duplicate an existing one.
				if (import.meta.env.DEV) {
					// eslint-disable-next-line no-console
					console.error(`Info-request status fetch failed for staff ${id}:`, err);
				}
				return null;
			}
		});

		if (run !== runRef.current) return; // a newer fanout superseded this one

		// Key by the id we asked for, not the one the response echoes back — the
		// table looks up by StaffMember.id, and a mismatch would silently render
		// every row as NOT_SENT.
		setStatuses(new Map(ids.map((id, index) => [id, entries[index]])));
		setIsLoading(false);
		// idsRef is a ref; idsKey stands in for the array identity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idsKey, locationId]);

	useEffect(() => {
		fetchStatuses();
	}, [fetchStatuses]);

	/**
	 * Patch one row after a send, avoiding a full refetch. Takes the staff id
	 * explicitly rather than reading `next.staffId`, to stay consistent with the
	 * fetch path — both key off the id the caller asked about, not the one the
	 * response echoes back.
	 */
	const applyStatus = useCallback((staffId: number, next: StaffInfoRequest) => {
		setStatuses(prev => new Map(prev).set(staffId, next));
	}, []);

	return { statuses, isLoading, refresh: fetchStatuses, applyStatus };
};

export default useStaffInfoRequestStatuses;
